import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  api,
  type StrengthRequest,
  type StrengthStats,
  type StrengthMissingReason,
  type StrengthStatsMissingEntry,
  type StrengthStatsRow,
  type StrengthResponderRow,
} from '@/lib/api';
import { renderNodeToPng, triggerDownload } from '@/lib/downloadPng';
import { estimateHint } from '@/lib/strengthEstimate';
import { useAllianceLabel } from '@/lib/useCurrentAlliance';

function todayISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

type Mode = 'request' | 'date';
type Anchor = 'startedAt' | 'closedAt';

interface RefPickerState {
  mode: Mode;
  requestId?: number;
  anchor: Anchor;
  date: string;
}

export default function StrengthStats() {
  const listQuery = useQuery({
    queryKey: ['strength-requests'],
    queryFn: api.listStrengthRequests,
  });

  const requests = listQuery.data ?? [];
  const [start, setStart] = useState<RefPickerState>({
    mode: 'request',
    anchor: 'closedAt',
    date: '',
  });
  // End picker default = today (custom date). Bleibt so, bis der User aktiv
  // etwas anderes wählt.
  const [end, setEnd] = useState<RefPickerState>({
    mode: 'date',
    anchor: 'closedAt',
    date: todayISO(),
  });

  // Default Start = vorletzter (second-to-last) closed request, damit die
  // Vergleichsspanne den letzten kompletten Monat abdeckt. Sobald aber schon
  // ein neuer Request offen läuft, ist der jüngste closed Request bereits die
  // sinnvolle Basis — dann den nehmen. End bleibt bei "heute".
  useEffect(() => {
    if (requests.length === 0) return;
    if (start.requestId) return;
    const closed = requests.filter((r) => !!r.closedAt);
    const hasOpenNewer = requests[0]?.status === 'open';
    const pick = hasOpenNewer ? closed[0] : closed[1] ?? closed[0];
    if (pick) {
      setStart((s) => ({
        ...s,
        mode: 'request',
        anchor: 'closedAt',
        requestId: pick.id,
        date: pick.closedAt ?? '',
      }));
    }
  }, [requests, start.requestId]);

  const startDate = resolveDate(start, requests);
  const endDate = resolveDate(end, requests);
  const canQuery = !!startDate && !!endDate;

  const [responderRequestId, setResponderRequestId] = useState<number | null>(
    null,
  );
  // Default: newest open request, fallback to newest overall.
  useEffect(() => {
    if (responderRequestId != null || requests.length === 0) return;
    const open = requests.find((r) => r.status === 'open');
    setResponderRequestId((open ?? requests[0]).id);
  }, [requests, responderRequestId]);
  const respondersQuery = useQuery({
    queryKey: ['strength-responders', responderRequestId],
    queryFn: () => api.getStrengthResponders(responderRequestId!),
    enabled: !!responderRequestId,
  });

  const statsQuery = useQuery({
    queryKey: ['strength-stats', startDate, endDate],
    queryFn: () =>
      api.getStrengthStats({ startDate: startDate!, endDate: endDate! }),
    enabled: canQuery,
  });

  return (
    <div className="space-y-4">
      <header>
        <Link
          to="/strength-requests"
          className="text-xs text-slate-400 hover:text-slate-200"
        >
          ← All requests
        </Link>
        <h1 className="mt-1 text-xl font-semibold">Compare Strength</h1>
        <p className="text-sm text-slate-400">
          Compare two points in time. Pick a request to prefill its start or
          close date, or enter a custom date.
        </p>
      </header>

      <div className="grid gap-4 md:grid-cols-2">
        <RefPicker
          label="Start"
          state={start}
          onChange={setStart}
          requests={requests}
        />
        <RefPicker
          label="End"
          state={end}
          onChange={setEnd}
          requests={requests}
        />
      </div>

      {!canQuery && (
        <div className="text-sm text-slate-500">
          Select two references to see stats.
        </div>
      )}

      {statsQuery.isLoading && canQuery && (
        <div className="text-slate-400">Loading…</div>
      )}

      {statsQuery.data && (
        <StatsView data={statsQuery.data} />
      )}

      <ResponderView
        requests={requests}
        selectedId={responderRequestId}
        onSelect={setResponderRequestId}
        data={respondersQuery.data ?? null}
        loading={respondersQuery.isLoading}
      />


      <Style />
    </div>
  );
}

function resolveDate(state: RefPickerState, requests: StrengthRequest[]) {
  if (state.mode === 'date') return state.date || null;
  if (!state.requestId) return null;
  const req = requests.find((r) => r.id === state.requestId);
  if (!req) return null;
  const value = state.anchor === 'closedAt' ? req.closedAt : req.startedAt;
  return value ? value.slice(0, 10) : null;
}

function RefPicker({
  label,
  state,
  onChange,
  requests,
}: {
  label: string;
  state: RefPickerState;
  onChange: (s: RefPickerState) => void;
  requests: StrengthRequest[];
}) {
  const closedRequests = useMemo(
    () => requests.filter((r) => !!r.closedAt),
    [requests],
  );
  return (
    <div className="space-y-2 rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-medium">{label}</h3>
        <div className="ml-auto flex rounded border border-slate-700 text-xs">
          <button
            className={
              state.mode === 'request'
                ? 'bg-indigo-600 px-2 py-1'
                : 'px-2 py-1 hover:bg-slate-800'
            }
            onClick={() => onChange({ ...state, mode: 'request' })}
          >
            Request
          </button>
          <button
            className={
              state.mode === 'date'
                ? 'bg-indigo-600 px-2 py-1'
                : 'px-2 py-1 hover:bg-slate-800'
            }
            onClick={() => onChange({ ...state, mode: 'date' })}
          >
            Custom date
          </button>
        </div>
      </div>
      {state.mode === 'request' ? (
        <>
          <select
            value={state.requestId ?? ''}
            onChange={(e) =>
              onChange({
                ...state,
                requestId: e.target.value ? Number(e.target.value) : undefined,
              })
            }
            className="input"
          >
            <option value="">— pick a request —</option>
            {closedRequests.map((r) => (
              <option key={r.id} value={r.id}>
                {r.title} ({r.closedAt?.slice(0, 10)})
              </option>
            ))}
            {requests
              .filter((r) => !r.closedAt)
              .map((r) => (
                <option key={r.id} value={r.id}>
                  {r.title} (open)
                </option>
              ))}
          </select>
          <div className="flex gap-3 text-xs text-slate-400">
            <label className="flex items-center gap-1">
              <input
                type="radio"
                checked={state.anchor === 'startedAt'}
                onChange={() => onChange({ ...state, anchor: 'startedAt' })}
              />
              Use started
            </label>
            <label className="flex items-center gap-1">
              <input
                type="radio"
                checked={state.anchor === 'closedAt'}
                onChange={() => onChange({ ...state, anchor: 'closedAt' })}
              />
              Use closed
            </label>
          </div>
        </>
      ) : (
        <input
          type="date"
          value={state.date}
          onChange={(e) => onChange({ ...state, date: e.target.value })}
          className="input"
        />
      )}
    </div>
  );
}

function StatsView({ data }: { data: StrengthStats }) {
  if (data.sampleSize === 0) {
    return (
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 text-sm text-slate-400">
        No player has reports within {data.maxGapDays} days of both dates.
        Nothing to compare yet.
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-4">
        <StatTile label="Avg Δ M1" value={data.averages.m1} />
        <StatTile label="Avg Δ M2" value={data.averages.m2} />
        <StatTile label="Avg Δ M3" value={data.averages.m3} />
        <StatTile label="Avg Δ M-Sum" value={data.averages.mSum} />
      </div>
      <div className="text-xs text-slate-500">
        Based on {data.sampleSize} of {data.totalActivePlayers} active players.
        Comparing {data.startDate} → {data.endDate}, same window for everyone:
        values are interpolated at both dates between each player's reports
        (≈ = nearest report a few days away).
        {data.missing.length > 0 && (
          <>
            {' '}
            {data.missing.length} not comparable (no report within{' '}
            {data.maxGapDays} days of a date).
          </>
        )}
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <RankTable title="Top 5 · Δ M1" rows={data.topM1} field="deltaM1" />
        <RankTable title="Flop 5 · Δ M1" rows={data.flopM1} field="deltaM1" />
        <RankTable title="Top 5 · Δ M-Sum" rows={data.topMSum} field="deltaMSum" />
        <RankTable title="Flop 5 · Δ M-Sum" rows={data.flopMSum} field="deltaMSum" />
      </div>
      <ExportInfographic data={data} />
      <AllPlayersTable rows={data.all} averages={data.averages} />
      {data.missing.length > 0 && (
        <MissingList rows={data.missing} maxGapDays={data.maxGapDays} />
      )}
    </div>
  );
}

type AllSortKey = 'name' | 'deltaM1' | 'deltaM2' | 'deltaM3' | 'deltaMSum';

function AllPlayersTable({
  rows,
  averages,
}: {
  rows: StrengthStatsRow[];
  averages: StrengthStats['averages'];
}) {
  const [sortKey, setSortKey] = useState<AllSortKey>('deltaMSum');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const toggle = (key: AllSortKey) => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  };
  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const cmp =
        sortKey === 'name'
          ? a.name.localeCompare(b.name)
          : a[sortKey] - b[sortKey];
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [rows, sortKey, sortDir]);
  return (
    <div className="overflow-hidden rounded-lg border border-slate-800 bg-slate-900/40">
      <div className="border-b border-slate-800 px-3 py-2 text-sm font-medium">
        All players
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-slate-900/60 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-1 text-left">#</th>
              <SortTh
                onClick={() => toggle('name')}
                active={sortKey === 'name'}
                dir={sortDir}
                align="left"
              >
                Player
              </SortTh>
              <SortTh
                onClick={() => toggle('deltaM1')}
                active={sortKey === 'deltaM1'}
                dir={sortDir}
              >
                M1
              </SortTh>
              <SortTh
                onClick={() => toggle('deltaM2')}
                active={sortKey === 'deltaM2'}
                dir={sortDir}
              >
                M2
              </SortTh>
              <SortTh
                onClick={() => toggle('deltaM3')}
                active={sortKey === 'deltaM3'}
                dir={sortDir}
              >
                M3
              </SortTh>
              <SortTh
                onClick={() => toggle('deltaMSum')}
                active={sortKey === 'deltaMSum'}
                dir={sortDir}
              >
                M-Sum
              </SortTh>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => (
              <tr
                key={r.playerId}
                className="border-t border-slate-800/60"
                title={estimateHint(r.fromEstimatedFrom, r.toEstimatedFrom)}
              >
                <td className="px-3 py-1 text-slate-500 tabular-nums">{i + 1}</td>
                <td className="px-3 py-1">
                  {r.name}
                  {r.displayName && (
                    <span className="ml-1 text-xs text-slate-500">
                      ({r.displayName})
                    </span>
                  )}
                </td>
                <FromToCell
                  from={r.fromM1}
                  to={r.toM1}
                  fromEstimated={!!r.fromEstimatedFrom}
                  toEstimated={!!r.toEstimatedFrom}
                  delta={r.deltaM1}
                  aboveAvg={r.deltaM1 > averages.m1}
                />
                <FromToCell
                  from={r.fromM2}
                  to={r.toM2}
                  fromEstimated={!!r.fromEstimatedFrom}
                  toEstimated={!!r.toEstimatedFrom}
                  delta={r.deltaM2}
                  aboveAvg={r.deltaM2 > averages.m2}
                />
                <FromToCell
                  from={r.fromM3}
                  to={r.toM3}
                  fromEstimated={!!r.fromEstimatedFrom}
                  toEstimated={!!r.toEstimatedFrom}
                  delta={r.deltaM3}
                  aboveAvg={r.deltaM3 > averages.m3}
                />
                <FromToCell
                  from={r.fromMSum}
                  to={r.toMSum}
                  fromEstimated={!!r.fromEstimatedFrom}
                  toEstimated={!!r.toEstimatedFrom}
                  delta={r.deltaMSum}
                  aboveAvg={r.deltaMSum > averages.mSum}
                  bold
                />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SortTh({
  children,
  onClick,
  active,
  dir,
  align = 'right',
}: {
  children: ReactNode;
  onClick: () => void;
  active: boolean;
  dir: 'asc' | 'desc';
  align?: 'left' | 'right';
}) {
  return (
    <th
      onClick={onClick}
      className={
        'cursor-pointer select-none px-3 py-1 ' +
        (align === 'right' ? 'text-right ' : 'text-left ') +
        (active ? 'text-white' : '')
      }
    >
      {children}
      {active && <span className="ml-1 text-[10px]">{dir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  );
}

function FromToCell({
  from,
  to,
  fromEstimated,
  toEstimated,
  delta,
  bold,
  aboveAvg,
}: {
  from: number;
  to: number;
  fromEstimated?: boolean;
  toEstimated?: boolean;
  delta: number;
  bold?: boolean;
  aboveAvg?: boolean;
}) {
  const pct = from > 0 ? (delta / from) * 100 : null;
  return (
    <td
      className={
        'px-3 py-1 text-right tabular-nums ' +
        (aboveAvg ? 'bg-emerald-500/10' : '')
      }
    >
      <div className={bold ? 'font-semibold' : ''}>
        <span className="text-slate-500">
          {fromEstimated && '≈'}
          {from.toFixed(2)}
        </span>
        <span className="mx-1 text-slate-600">→</span>
        <span>
          {toEstimated && '≈'}
          {to.toFixed(2)}
        </span>
      </div>
      <div
        className={
          'text-xs ' + (delta >= 0 ? 'text-emerald-300' : 'text-red-300')
        }
      >
        {aboveAvg && (
          <span
            title="Above alliance average"
            className="mr-1 text-emerald-400"
          >
            ★
          </span>
        )}
        {delta >= 0 ? '+' : ''}
        {delta.toFixed(2)}
        {pct !== null && (
          <span className="ml-1 text-slate-500">
            ({delta >= 0 ? '+' : ''}
            {pct.toFixed(1)}%)
          </span>
        )}
      </div>
    </td>
  );
}

const MISSING_REASON_LABEL: Record<StrengthMissingReason, (days: number) => string> = {
  no_report_near_start: (d) => `No report within ${d} days of the start date`,
  no_report_near_end: (d) => `No report within ${d} days of the end date`,
  single_report: () => 'Only one report around the window',
};

function MissingList({
  rows,
  maxGapDays,
}: {
  rows: StrengthStatsMissingEntry[];
  maxGapDays: number;
}) {
  const groups = (Object.keys(MISSING_REASON_LABEL) as StrengthMissingReason[])
    .map((reason) => ({ reason, rows: rows.filter((r) => r.reason === reason) }))
    .filter((g) => g.rows.length > 0);
  return (
    <div className="space-y-2 rounded-lg border border-slate-800 bg-slate-900/40 p-3 text-sm">
      <div className="text-xs uppercase tracking-wider text-slate-500">
        Not comparable ({rows.length})
      </div>
      {groups.map((g) => (
        <div key={g.reason}>
          <div className="mb-0.5 text-xs text-slate-500">
            {MISSING_REASON_LABEL[g.reason](maxGapDays)} ({g.rows.length})
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-400">
            {g.rows.map((r) => (
              <span key={r.playerId}>
                {r.name}
                {r.displayName && (
                  <span className="text-slate-600"> ({r.displayName})</span>
                )}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ResponderView({
  requests,
  selectedId,
  onSelect,
  data,
  loading,
}: {
  requests: StrengthRequest[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  data: import('@/lib/api').StrengthResponders | null;
  loading: boolean;
}) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base font-semibold">Fast responders</h2>
        <select
          value={selectedId ?? ''}
          onChange={(e) =>
            e.target.value ? onSelect(Number(e.target.value)) : undefined
          }
          className="input max-w-xs"
        >
          <option value="">— pick a request —</option>
          {requests.map((r) => (
            <option key={r.id} value={r.id}>
              {r.title} ({r.status})
            </option>
          ))}
        </select>
      </div>
      {!selectedId && (
        <div className="text-sm text-slate-500">
          Pick a request to see response times.
        </div>
      )}
      {loading && selectedId && (
        <div className="text-sm text-slate-400">Loading…</div>
      )}
      {data && (
        <>
          <div className="text-xs text-slate-500">
            {data.submittedCount} of {data.totalActivePlayers} submitted since{' '}
            {data.startedAt.slice(0, 16).replace('T', ' ')}.
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <ResponderTable title="Top 5 · Fastest" rows={data.fastest} />
            <ResponderTable title="Flop 5 · Slowest" rows={data.slowest} />
          </div>
        </>
      )}
    </div>
  );
}

function ResponderTable({
  title,
  rows,
}: {
  title: string;
  rows: StrengthResponderRow[];
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-slate-800 bg-slate-900/40">
      <div className="border-b border-slate-800 px-3 py-2 text-sm font-medium">
        {title}
      </div>
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wider text-slate-500">
          <tr>
            <th className="px-3 py-1 text-left">#</th>
            <th className="px-3 py-1 text-left">Player</th>
            <th className="px-3 py-1 text-right">Response time</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.playerId} className="border-t border-slate-800/60">
              <td className="px-3 py-1 text-slate-500">{i + 1}</td>
              <td className="px-3 py-1">
                {r.name}
                {r.displayName && (
                  <span className="ml-1 text-xs text-slate-500">
                    ({r.displayName})
                  </span>
                )}
              </td>
              <td className="px-3 py-1 text-right tabular-nums text-slate-300">
                {formatDuration(r.responseSeconds)}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={3} className="px-3 py-4 text-center text-slate-500">
                —
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function formatDuration(seconds: number) {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  if (h < 48) return `${h}h ${remM}m`;
  const d = Math.floor(h / 24);
  const remH = h % 24;
  return `${d}d ${remH}h`;
}

function StatTile({ label, value }: { label: string; value: number }) {
  const positive = value >= 0;
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <div className="text-xs uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div
        className={
          'mt-1 text-2xl font-semibold tabular-nums ' +
          (positive ? 'text-emerald-300' : 'text-red-300')
        }
      >
        {positive ? '+' : ''}
        {value.toFixed(2)}
      </div>
    </div>
  );
}

function RankTable({
  title,
  rows,
  field,
}: {
  title: string;
  rows: StrengthStatsRow[];
  field: 'deltaM1' | 'deltaMSum';
}) {
  const fromField = field === 'deltaM1' ? 'fromM1' : 'fromMSum';
  const toField = field === 'deltaM1' ? 'toM1' : 'toMSum';
  return (
    <div className="overflow-hidden rounded-lg border border-slate-800 bg-slate-900/40">
      <div className="border-b border-slate-800 px-3 py-2 text-sm font-medium">
        {title}
      </div>
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wider text-slate-500">
          <tr>
            <th className="px-3 py-1 text-left">#</th>
            <th className="px-3 py-1 text-left">Player</th>
            <th className="px-3 py-1 text-right">Before</th>
            <th className="px-3 py-1 text-right">After</th>
            <th className="px-3 py-1 text-right">Δ</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const delta = r[field];
            return (
              <tr
                key={r.playerId}
                className="border-t border-slate-800/60"
                title={estimateHint(r.fromEstimatedFrom, r.toEstimatedFrom)}
              >
                <td className="px-3 py-1 text-slate-500 tabular-nums">{i + 1}</td>
                <td className="px-3 py-1">
                  {r.name}
                  {r.displayName && (
                    <span className="ml-1 text-xs text-slate-500">
                      ({r.displayName})
                    </span>
                  )}
                </td>
                <td className="px-3 py-1 text-right tabular-nums text-slate-400">
                  {r.fromEstimatedFrom && '≈'}
                  {r[fromField].toFixed(2)}
                </td>
                <td className="px-3 py-1 text-right tabular-nums">
                  {r.toEstimatedFrom && '≈'}
                  {r[toField].toFixed(2)}
                </td>
                <td
                  className={
                    'px-3 py-1 text-right tabular-nums font-medium ' +
                    (delta >= 0 ? 'text-emerald-300' : 'text-red-300')
                  }
                >
                  {delta >= 0 ? '+' : ''}
                  {delta.toFixed(2)}
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td colSpan={5} className="px-3 py-4 text-center text-slate-500">
                —
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

type ExportField = 'M1' | 'M2' | 'M3' | 'MSum';

const EXPORT_FIELDS: {
  key: ExportField;
  label: string;
  fileKey: string;
  deltaKey: keyof StrengthStatsRow;
  fromKey: keyof StrengthStatsRow;
  toKey: keyof StrengthStatsRow;
  avgKey: keyof StrengthStats['averages'];
}[] = [
  { key: 'M1', label: 'Δ M1', fileKey: 'm1', deltaKey: 'deltaM1', fromKey: 'fromM1', toKey: 'toM1', avgKey: 'm1' },
  { key: 'M2', label: 'Δ M2', fileKey: 'm2', deltaKey: 'deltaM2', fromKey: 'fromM2', toKey: 'toM2', avgKey: 'm2' },
  { key: 'M3', label: 'Δ M3', fileKey: 'm3', deltaKey: 'deltaM3', fromKey: 'fromM3', toKey: 'toM3', avgKey: 'm3' },
  { key: 'MSum', label: 'Δ M-Sum', fileKey: 'msum', deltaKey: 'deltaMSum', fromKey: 'fromMSum', toKey: 'toMSum', avgKey: 'mSum' },
];

function ExportInfographic({ data }: { data: StrengthStats }) {
  const [busy, setBusy] = useState<ExportField | null>(null);
  const refs: Record<ExportField, React.RefObject<HTMLDivElement | null>> = {
    M1: useRef<HTMLDivElement>(null),
    M2: useRef<HTMLDivElement>(null),
    M3: useRef<HTMLDivElement>(null),
    MSum: useRef<HTMLDivElement>(null),
  };

  const rowsByField = useMemo(() => {
    const out = {} as Record<ExportField, StrengthStatsRow[]>;
    for (const f of EXPORT_FIELDS) {
      out[f.key] = [...data.all]
        .sort(
          (a, b) =>
            (b[f.deltaKey] as number) - (a[f.deltaKey] as number),
        )
        .slice(0, 100);
    }
    return out;
  }, [data.all]);

  const download = async (field: ExportField) => {
    const node = refs[field].current;
    if (!node) return;
    const meta = EXPORT_FIELDS.find((f) => f.key === field)!;
    setBusy(field);
    try {
      const dataUrl = await renderNodeToPng(node, {
        backgroundColor: '#0f172a',
        pixelRatio: 1.5,
        format: 'jpeg',
        quality: 0.9,
      });
      const name = `strength-top100-${meta.fileKey}-${data.startDate}_to_${data.endDate}.jpg`;
      triggerDownload(dataUrl, name);
    } catch (err) {
      console.error(err);
      alert('Export failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-sm font-medium">Export infographic (Top 100)</div>
        <div className="ml-auto flex flex-wrap gap-2">
          {EXPORT_FIELDS.map((f) => (
            <button
              key={f.key}
              onClick={() => download(f.key)}
              disabled={busy !== null || rowsByField[f.key].length === 0}
              className="rounded bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
            >
              {busy === f.key ? 'Rendering…' : `Download · ${f.label}`}
            </button>
          ))}
        </div>
      </div>
      <p className="mt-1 text-xs text-slate-500">
        Renders a 5×20 grid JPG. Each entry shows rank, player name, Δ and
        current value.
      </p>
      {/* Offscreen render targets */}
      <div
        aria-hidden
        style={{
          position: 'fixed',
          left: -99999,
          top: 0,
          pointerEvents: 'none',
        }}
      >
        {EXPORT_FIELDS.map((f) => (
          <InfographicCard
            key={f.key}
            ref={refs[f.key]}
            title={`Top 100 · ${f.label}`}
            data={data}
            rows={rowsByField[f.key]}
            field={f.key}
          />
        ))}
      </div>
    </div>
  );
}

const InfographicCard = ({
  ref,
  title,
  data,
  rows,
  field,
}: {
  ref: React.RefObject<HTMLDivElement | null>;
  title: string;
  data: StrengthStats;
  rows: StrengthStatsRow[];
  field: ExportField;
}) => {
  const allianceLabel = useAllianceLabel();
  const meta = EXPORT_FIELDS.find((f) => f.key === field)!;
  const avgValue = data.averages[meta.avgKey];
  const fieldLabel = field === 'MSum' ? 'M-Sum' : field;
  const COLS = 5;
  const ROWS = 20;
  type LayoutCell = (StrengthStatsRow & { rank: number }) | null;
  // Columns fill top-to-bottom (ranks 1..20 in col 1, 21..40 in col 2, …).
  // The CSS grid renders row-major, so re-index accordingly.
  const layout: LayoutCell[] = [];
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      const idx = c * ROWS + r;
      const src = rows[idx];
      layout.push(src ? { ...src, rank: idx + 1 } : null);
    }
  }
  return (
    <div
      ref={ref}
      style={{
        width: 1600,
        padding: 32,
        background: 'linear-gradient(180deg,#0f172a 0%,#0b1220 100%)',
        color: '#e2e8f0',
        fontFamily:
          'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'space-between',
          gap: 20,
          borderBottom: '1px solid #1e293b',
          paddingBottom: 16,
          marginBottom: 20,
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, color: '#64748b', letterSpacing: 2 }}>
            {allianceLabel} · Alliance Strength
          </div>
          <div style={{ fontSize: 34, fontWeight: 700, marginTop: 4 }}>
            {title}
          </div>
          <div style={{ marginTop: 6, fontSize: 13, color: '#94a3b8' }}>
            {data.startDate} <span style={{ color: '#475569' }}>→</span>{' '}
            {data.endDate} · Sample {data.sampleSize} /{' '}
            {data.totalActivePlayers} active
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minWidth: 240,
            padding: '10px 20px',
            borderRadius: 12,
            background:
              'linear-gradient(135deg, rgba(16,185,129,0.18), rgba(56,189,248,0.12))',
            border: '1px solid rgba(16,185,129,0.55)',
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: '#94a3b8',
              letterSpacing: 2,
              textTransform: 'uppercase',
            }}
          >
            Alliance avg · Δ {fieldLabel}
          </div>
          <div
            style={{
              marginTop: 4,
              fontSize: 40,
              fontWeight: 800,
              fontVariantNumeric: 'tabular-nums',
              color: '#6ee7b7',
              lineHeight: 1,
            }}
          >
            {avgValue >= 0 ? '+' : ''}
            {avgValue.toFixed(2)}
          </div>
          <div style={{ marginTop: 4, fontSize: 11, color: '#64748b' }}>
            ★ marks players above this benchmark
          </div>
        </div>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${COLS}, 1fr)`,
          gap: 8,
        }}
      >
        {layout.map((row, i) =>
          row ? (
            <InfoCell
              key={i}
              row={row}
              field={field}
              rank={row.rank}
              avg={avgValue}
            />
          ) : (
            <div key={i} />
          ),
        )}
      </div>
      <div
        style={{
          marginTop: 20,
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 11,
          color: '#475569',
        }}
      >
        <div>
          <span style={{ color: '#34d399' }}>★</span> above alliance average
        </div>
        <div>
          Ranked by Δ {fieldLabel} · values shown are current March-
          {field === 'MSum' ? 'sum' : `value ${field}`}
        </div>
      </div>
    </div>
  );
};

function InfoCell({
  row,
  field,
  rank,
  avg,
}: {
  row: StrengthStatsRow;
  field: ExportField;
  rank: number;
  avg: number;
}) {
  const meta = EXPORT_FIELDS.find((f) => f.key === field)!;
  const delta = row[meta.deltaKey] as number;
  const before = row[meta.fromKey] as number;
  const current = row[meta.toKey] as number;
  const positive = delta >= 0;
  const aboveAvg = delta > avg;
  const rankColor =
    rank <= 3
      ? '#facc15'
      : rank <= 10
        ? '#a3e635'
        : rank <= 25
          ? '#38bdf8'
          : '#475569';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        background: aboveAvg
          ? 'rgba(16,185,129,0.12)'
          : 'rgba(30,41,59,0.55)',
        border: `1px solid ${aboveAvg ? '#10b981' : '#1e293b'}`,
        borderRadius: 8,
        padding: '8px 10px',
        minHeight: 52,
      }}
    >
      <div
        style={{
          width: 34,
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 700,
          fontSize: 18,
          color: rankColor,
          flexShrink: 0,
        }}
      >
        {rank}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: '#f1f5f9',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {aboveAvg && (
            <span style={{ color: '#34d399', marginRight: 4 }}>★</span>
          )}
          {row.name}
        </div>
      </div>
      <div
        style={{
          textAlign: 'right',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          lineHeight: 1.15,
        }}
      >
        <div
          style={{
            fontSize: 15,
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
            color: positive ? '#6ee7b7' : '#fca5a5',
          }}
        >
          {positive ? '+' : ''}
          {delta.toFixed(2)}
        </div>
        <div
          style={{
            fontSize: 11,
            color: '#64748b',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <span>{before.toFixed(2)}</span>
          <span style={{ margin: '0 4px', color: '#475569' }}>→</span>
          <span style={{ color: '#94a3b8' }}>{current.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}

function Style() {
  return (
    <style>{`
      .input {
        width: 100%;
        background: rgb(2 6 23 / 1);
        border: 1px solid rgb(51 65 85 / 1);
        border-radius: 6px;
        padding: 6px 10px;
        font-size: 14px;
        color: inherit;
      }
      .input:focus { outline: none; border-color: rgb(99 102 241 / 1); }
    `}</style>
  );
}
